import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { msg } from '@gonfalon/messages/msg';

import { AggregationType, Granularity, type TimeRangeOption, UsageTab } from '../../../../types';
import { useDataExportSourceUsage } from '../../../hooks/useDataExportSourceUsage';
import {
  DataExportScope,
  type DataExportSource,
  type DataExportSourceStates,
} from '../../../utils/combineDataExportUsage';
import { getBreakdownPreviousRange } from '../../../utils/dataExportBreakdown';
import { getSeriesTotal } from '../../../utils/getSeriesTotal';
import { getUsageSummaryTooltip } from '../../../utils/getUsageDisplayName';

import { BaseSummaryCard } from './UsageSummaryCard';

import styles from './UsageSummaryCards.module.css';

type DataExportCombinedSummaryCardsProps = {
  fromTimestampUTC: number;
  toTimestampUTC: number;
  timeRange?: TimeRangeOption;
  today: Date;
  precision?: number;
  percentChangeTooltip: string;
  showSourceCards: boolean;
};

function getSourceTotal(sources: DataExportSourceStates, source: DataExportSource) {
  const data = sources[source].data;
  return Array.from({ length: Math.max(1, data?.metadata.length ?? 0) }, (_, index) =>
    getSeriesTotal({
      series: data?.series,
      aggregationType: AggregationType.INCREMENTAL,
      granularity: Granularity.DAILY,
      metadataIdx: String(index),
    }),
  ).reduce((sum, total) => sum + total, 0);
}

function DataExportCombinedSummaryCardsContent({
  fromTimestampUTC,
  toTimestampUTC,
  timeRange,
  today,
  precision,
  percentChangeTooltip,
  showSourceCards,
}: DataExportCombinedSummaryCardsProps) {
  const previousRange = getBreakdownPreviousRange({ timeRange, from: fromTimestampUTC, to: toTimestampUTC, today });
  const current = useDataExportSourceUsage({
    from: fromTimestampUTC,
    to: toTimestampUTC,
    aggregationType: AggregationType.INCREMENTAL,
    granularity: Granularity.DAILY,
  });
  const previous = useDataExportSourceUsage({
    ...previousRange,
    aggregationType: AggregationType.INCREMENTAL,
    granularity: Granularity.DAILY,
  });

  const card = (source: DataExportSource) => ({
    total: getSourceTotal(current, source),
    isTotalLoading: current[source].isLoading,
    isTotalError: current[source].isError,
    previousTotal: getSourceTotal(previous, source),
    isPreviousLoading: previous[source].isLoading,
    isPreviousError: previous[source].isError,
  });
  const streaming = card(DataExportScope.STREAMING);
  const warehouse = card(DataExportScope.WAREHOUSE);
  const shared = { percentChangeTooltip, precision };

  return (
    <div className={styles.cardsContainer}>
      <div className={styles.fullValueSummary}>
        <BaseSummaryCard
          title={msg('billing.usageSummary.total')}
          titleTooltip={getUsageSummaryTooltip(UsageTab.DATA_EXPORT_COMBINED)}
          total={streaming.total + warehouse.total}
          isTotalLoading={
            (streaming.isTotalLoading || warehouse.isTotalLoading) &&
            !(streaming.isTotalError || warehouse.isTotalError)
          }
          isTotalError={streaming.isTotalError || warehouse.isTotalError}
          previousTotal={streaming.previousTotal + warehouse.previousTotal}
          isPreviousLoading={streaming.isPreviousLoading || warehouse.isPreviousLoading}
          isPreviousError={streaming.isPreviousError || warehouse.isPreviousError}
          {...shared}
        />
        {showSourceCards && (
          <>
            <BaseSummaryCard
              title={msg('billing.dataExportCombined.streamingExports')}
              titleTooltip={getUsageSummaryTooltip(UsageTab.DATA_EXPORT)}
              {...streaming}
              {...shared}
            />
            <BaseSummaryCard
              title={msg('billing.dataExportCombined.warehouseExports')}
              titleTooltip={getUsageSummaryTooltip(UsageTab.WAREHOUSE_DATA_EXPORT)}
              {...warehouse}
              {...shared}
            />
          </>
        )}
      </div>
    </div>
  );
}

export function DataExportCombinedSummaryCards(props: DataExportCombinedSummaryCardsProps) {
  return (
    <QuietErrorBoundary>
      <DataExportCombinedSummaryCardsContent {...props} />
    </QuietErrorBoundary>
  );
}
